import { Component, Input, Output, EventEmitter, OnInit, OnDestroy } from '@angular/core';
import { Subject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { PublishingStatus, PublishingDateRangeKey } from '../../../domain/publishing-status';
import { PUBLISHING_DATE_RANGE_OPTIONS } from '../publishing-status.constants';

@Component({
  selector: 'evrideo-publishing-toolbar',
  templateUrl: './publishing-toolbar.component.html',
  styleUrls: ['./publishing-toolbar.component.scss'],
})
export class PublishingToolbarComponent implements OnInit, OnDestroy {
  readonly dateRangeOptions = PUBLISHING_DATE_RANGE_OPTIONS;

  @Input() searchQuery: string = '';
  @Input() statusFilter: PublishingStatus | 'All' = 'All';
  @Input() dateRangeKey: PublishingDateRangeKey = 'all';
  @Input() selectedCount = 0;
  @Input() isExportingToMam = false;
  @Input() isDeleting = false;

  @Output() searchChange = new EventEmitter<string>();
  @Output() statusChange = new EventEmitter<PublishingStatus | 'All'>();
  @Output() dateRangeChange = new EventEmitter<PublishingDateRangeKey>();
  @Output() startNew = new EventEmitter<void>();
  @Output() importToMam = new EventEmitter<void>();
  @Output() deleteSelected = new EventEmitter<void>();

  private searchSubject = new Subject<string>();
  private searchSub?: Subscription;

  get isBatchBusy(): boolean {
    return this.isExportingToMam || this.isDeleting;
  }

  get canImportToMam(): boolean {
    return this.selectedCount > 0 && !this.isBatchBusy;
  }

  get canDelete(): boolean {
    return this.selectedCount > 0 && !this.isBatchBusy;
  }

  get importToMamLabel(): string {
    return this.isExportingToMam ? 'Processing…' : 'Import to MAM';
  }

  get deleteLabel(): string {
    return this.isDeleting ? 'Deleting…' : 'Delete';
  }

  ngOnInit(): void {
    this.searchSub = this.searchSubject
      .pipe(debounceTime(1000), distinctUntilChanged())
      .subscribe((query) => this.searchChange.emit(query));
  }

  ngOnDestroy(): void {
    this.searchSub?.unsubscribe();
  }

  onSearchValueChange(value: string): void {
    this.searchSubject.next(value ?? '');
  }

  onStatusValueChange(value: PublishingStatus | 'All'): void {
    this.statusChange.emit(value);
  }

  onDateRangeValueChange(value: PublishingDateRangeKey): void {
    this.dateRangeChange.emit(value);
  }

  onClearSearch(): void {
    this.searchSubject.next('');
    this.searchChange.emit('');
  }

  onStartNew(): void {
    this.startNew.emit();
  }

  onImportToMamClick(): void {
    if (!this.canImportToMam) {
      return;
    }
    this.importToMam.emit();
  }

  onDeleteClick(): void {
    if (!this.canDelete) {
      return;
    }
    this.deleteSelected.emit();
  }
}
